import { Trans } from "@lingui/react/macro";
import { Button, ButtonProps } from "@silversea-ssc/ui-components-react";
import React, { ReactElement } from "react";
import {
  ABTestDomAttr,
  useExperimentTag
} from "src/ab-test-experiments/client/use-experiment-tag";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { isPresent } from "src/utils/checks";
import { match } from "ts-pattern";
import { useAlgoliaConversionTracking } from "./Algolia/algoliaAnalytics";

type Props = {
  ecommerceStatus: ECommerceStatus | undefined;
  raqUrl: string | undefined;
  openWebQuoteModal?: () => void;
  size: ButtonProps["size"];
  className?: string;
  disableSecondaryRAQ?: boolean;
  inverted?: boolean;
  overrideEcommerceLabel?: ReactElement;
};

const ECommerceButtons: React.FC<Props> = ({
  ecommerceStatus = { type: "disabled" },
  raqUrl,
  size,
  disableSecondaryRAQ = false,
  className,
  inverted = false,
  openWebQuoteModal,
  overrideEcommerceLabel
}) => {
  const { onECommerceClick, onRAQClick } = useAlgoliaConversionTracking();
  const isWebQuoteExperimentActive = useExperimentTag("web-quote-modal");

  const showWebQuoteButton =
    isPresent(openWebQuoteModal) &&
    isWebQuoteExperimentActive?.segmentId === "b";

  if (ecommerceStatus.type == "loading") return null;

  return match(ecommerceStatus)
    .with({ type: "disabled" }, () => (
      <RAQButton
        url={raqUrl}
        variant="accent"
        size={size}
        className={className}
        inverted={inverted}
        onClick={onRAQClick}
      />
    ))
    .with({ type: "ecommerce" }, ({ ecommerceUrl }) => {
      return (
        <>
          <ECommerceButton
            url={ecommerceUrl}
            size={size}
            className={className}
            variant="accent"
            inverted={inverted}
            onClick={onECommerceClick}
            overrideLabel={overrideEcommerceLabel}
          />

          {!disableSecondaryRAQ && !showWebQuoteButton && (
            <RAQButton
              url={raqUrl}
              variant="text"
              size={size}
              className={className}
              inverted={inverted}
              onClick={onRAQClick}
            />
          )}
        </>
      );
    })
    .with({ type: "priceconfig" }, ({ url }) => {
      return (
        <React.Fragment>
          <PriceConfigButton
            url={url}
            size={size}
            className={className}
            variant="accent"
            inverted={inverted}
            onClick={onECommerceClick}
          />
          {!disableSecondaryRAQ && !showWebQuoteButton && (
            <RAQButton
              url={raqUrl}
              variant="text"
              size={size}
              className={className}
              inverted={inverted}
              onClick={onRAQClick}
            />
          )}
        </React.Fragment>
      );
    })
    .exhaustive();
};

export default ECommerceButtons;

type NewEcommerceButtonProps = Omit<
  Props,
  | "enableExperimentRAQtoPriceConfig"
  | "disableSecondaryRAQ"
  | "openWebQuoteModal"
> & {
  variant?: ButtonProps["variant"];
  overrideEcommerceLabel?: ReactElement;
  disableRAQ?: boolean;
  inverted?: boolean;
  full?: boolean;
} & Partial<ABTestDomAttr>;

export const NewEcommerceButton: React.FC<NewEcommerceButtonProps> = ({
  raqUrl,
  size,
  ecommerceStatus = { type: "disabled" },
  variant = "primary",
  className,
  overrideEcommerceLabel,
  disableRAQ = false,
  ...props
}) => {
  const { onECommerceClick, onRAQClick } = useAlgoliaConversionTracking();
  if (ecommerceStatus.type == "loading") return null;

  return match(ecommerceStatus)
    .with({ type: "disabled" }, () =>
      disableRAQ ? null : (
        <RAQButton
          url={raqUrl}
          variant={variant}
          size={size}
          className={className}
          onClick={onRAQClick}
          {...props}
        />
      )
    )
    .with({ type: "ecommerce" }, ({ ecommerceUrl }) => (
      <ECommerceButton
        url={ecommerceUrl}
        size={size}
        className={className}
        variant={variant}
        overrideLabel={overrideEcommerceLabel}
        onClick={onECommerceClick}
        {...props}
      />
    ))
    .with({ type: "priceconfig" }, ({ url }) => (
      <PriceConfigButton
        url={url}
        size={size}
        className={className}
        variant={variant}
        onClick={onECommerceClick}
        {...props}
      />
    ))
    .exhaustive();
};

type EcommerceButtonProps = {
  size: ButtonProps["size"];
  variant: ButtonProps["variant"];
  className?: string;
  url?: string;
  overrideLabel?: ReactElement;
  inverted?: boolean;
  onClick?: () => void;
} & Partial<ABTestDomAttr>;

const ECommerceButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  overrideLabel,
  onClick,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="book_your_cruise"
      noWrap
      size={size}
      className={className}
      onClick={onClick}
      onAuxClick={onClick}
      {...props}
    >
      {overrideLabel ? (
        <React.Fragment>{overrideLabel}</React.Fragment>
      ) : (
        <Trans id="ecommerce-button-label">Book your Cruise</Trans>
      )}
    </Button>
  );
};

const PriceConfigButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="plan_your_cruise"
      noWrap
      size={size}
      className={className}
      {...props}
    >
      <Trans id="priceconfig-button-label">Plan your Cruise</Trans>
    </Button>
  );
};

export const WebQuoteButton: React.FC<
  Omit<EcommerceButtonProps, "url"> & { onClick: () => void }
> = ({ size, className, variant, onClick, ...props }) => {
  const { onWebQuotelick } = useAlgoliaConversionTracking();

  if (!isPresent(onClick)) return null;

  return (
    <Button
      variant={variant}
      onClick={() => onWebQuotelick(onClick)}
      dataContentPiece="web_quote"
      size={size}
      className={className}
      {...props}
    >
      <Trans>Save this quote</Trans>
    </Button>
  );
};

export const RAQButton: React.FC<EcommerceButtonProps> = ({
  size,
  className,
  url,
  variant,
  ...props
}) => {
  if (!isPresent(url)) return null;

  return (
    <Button
      variant={variant}
      href={url}
      dataContentPiece="request_a_quote"
      size={size}
      className={className}
      {...props}
    >
      <Trans>Request a Quote</Trans>
    </Button>
  );
};
